import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Card,
  CardContent,
  IconButton,
  Chip,
  Avatar,
  Stack,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  ThumbUp as ThumbUpIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  BugReport as BugIcon,
  Lightbulb as FeatureIcon,
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { getUserDetail, getUserId } from '../../constants/function';
import Back from '../../components/Back';
import ApiService from '../../services/api-service';
import notifier from '../../components/Notifier';
import './index.scss';

const COLUMNS = [
  { id: 'list', title: 'Submitted', color: '#2196F3' },
  { id: 'inprocess', title: 'In Process', color: '#FF9800' },
  { id: 'testing', title: 'Testing', color: '#9C27B0' },
  { id: 'finished', title: 'Finished', color: '#4CAF50' },
];

export default function FeedbackHub() {
  const [open, setOpen] = useState(false);
  const [tickets, setTickets] = useState({
    list: [],
    inprocess: [],
    testing: [],
    finished: [],
  });
  const [newTicket, setNewTicket] = useState({
    title: '',
    description: '',
    type: 'bug', // 'bug' or 'feature'
  });

  const userDetail = getUserDetail();
  const userId = getUserId();
  const isAdmin = userDetail?.email === 'chris@siki.io';

  useEffect(() => {
    // TODO: Fetch tickets from API
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await ApiService.FEEDBACK_GET_ALL();
      if (response.success) {
        setTickets(response.result);
      } else {
        notifier.error(response.message || 'Failed to fetch feedback');
        // Initialize with empty state
        setTickets({
          list: [],
          inprocess: [],
          testing: [],
          finished: []
        });
      }
    } catch (error) {
      console.error('Error fetching feedback:', error);
      notifier.error('Failed to fetch feedback');
      // Initialize with empty state
      setTickets({
        list: [],
        inprocess: [],
        testing: [],
        finished: []
      });
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.title.trim() || !newTicket.description.trim()) {
      notifier.error('Please fill in all fields');
      return;
    }

    try {
      const response = await ApiService.FEEDBACK_CREATE({
        title: newTicket.title.trim(),
        description: newTicket.description.trim(),
        type: newTicket.type
      });

      if (response.success) {
        notifier.success('Feedback submitted successfully');
        // Refresh the list to get the new ticket with proper ID from backend
        await fetchTickets();
        setNewTicket({ title: '', description: '', type: 'bug' });
        setOpen(false);
      } else {
        notifier.error(response.message || 'Failed to create feedback');
      }
    } catch (error) {
      console.error('Error creating feedback:', error);
      notifier.error('Failed to submit feedback');
    }
  };

  const handleVote = async (ticketId, columnId) => {
    // Optimistically update UI first
    const ticket = tickets[columnId].find(t => t.id === ticketId);
    if (!ticket) return;

    const hasVoted = ticket.votedBy && ticket.votedBy.some(v => 
      (typeof v === 'string' ? v === userId : v.user === userId)
    );
    const voteType = hasVoted ? 'down' : 'up';

    try {
      const response = await ApiService.FEEDBACK_VOTE(ticketId, voteType);
      
      if (response.success) {
        // Update the local state with the server response
        setTickets(prev => {
          const column = prev[columnId];
          const ticketIndex = column.findIndex(t => t.id === ticketId);
          if (ticketIndex === -1) return prev;

          const updatedColumn = [...column];
          updatedColumn[ticketIndex] = {
            ...updatedColumn[ticketIndex],
            votes: response.result.votes,
            votedBy: response.result.votedBy
          };

          return {
            ...prev,
            [columnId]: updatedColumn,
          };
        });
      } else {
        notifier.error(response.message || 'Failed to vote');
        // Refresh to sync with server state
        await fetchTickets();
      }
    } catch (error) {
      console.error('Error voting:', error);
      notifier.error('Failed to vote');
      // Refresh to sync with server state
      await fetchTickets();
    }
  };

  const handleDeleteTicket = async (ticketId, columnId) => {
    try {
      const response = await ApiService.FEEDBACK_DELETE(ticketId);
      
      if (response.success) {
        notifier.success('Feedback deleted successfully');
        // Remove from local state
        setTickets(prev => ({
          ...prev,
          [columnId]: prev[columnId].filter(t => t.id !== ticketId),
        }));
      } else {
        notifier.error(response.message || 'Failed to delete feedback');
      }
    } catch (error) {
      console.error('Error deleting feedback:', error);
      notifier.error('Failed to delete feedback');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination || !isAdmin) return;

    const { source, destination } = result;
    
    if (source.droppableId === destination.droppableId) {
      // Reordering within same column - just update UI, no API call needed
      const column = [...tickets[source.droppableId]];
      const [removed] = column.splice(source.index, 1);
      column.splice(destination.index, 0, removed);
      
      setTickets(prev => ({
        ...prev,
        [source.droppableId]: column,
      }));
    } else {
      // Moving between columns - need to update status
      const sourceColumn = [...tickets[source.droppableId]];
      const destColumn = [...tickets[destination.droppableId]];
      const [movedTicket] = sourceColumn.splice(source.index, 1);
      destColumn.splice(destination.index, 0, movedTicket);
      
      // Optimistically update UI
      setTickets(prev => ({
        ...prev,
        [source.droppableId]: sourceColumn,
        [destination.droppableId]: destColumn,
      }));

      // Update status in backend
      try {
        const response = await ApiService.FEEDBACK_UPDATE_STATUS(
          movedTicket.id, 
          destination.droppableId
        );
        
        if (!response.success) {
          notifier.error(response.message || 'Failed to update status');
          // Revert on failure
          await fetchTickets();
        }
      } catch (error) {
        console.error('Error updating ticket status:', error);
        notifier.error('Failed to update ticket status');
        // Revert on failure
        await fetchTickets();
      }
    }
  };

  const canDelete = (ticket) => {
    return isAdmin || ticket.author.id === userId;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Back />
      
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
          Feedback Hub
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Share your ideas and report bugs to help us improve SIKI. Premium users can submit feedback and vote on existing suggestions.
        </Typography>
        
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
          sx={{
            backgroundColor: '#2196F3',
            '&:hover': { backgroundColor: '#1976D2' },
          }}
        >
          Submit Feedback
        </Button>
      </Box>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Grid container spacing={3}>
          {COLUMNS.map((column) => (
            <Grid item xs={12} md={3} key={column.id}>
              <Card sx={{ height: '100%', minHeight: 600 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: column.color,
                        mr: 1,
                      }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {column.title}
                    </Typography>
                    <Chip
                      label={tickets[column.id].length}
                      size="small"
                      sx={{ ml: 'auto', backgroundColor: column.color, color: 'white' }}
                    />
                  </Box>

                  <Droppable droppableId={column.id} isDropDisabled={!isAdmin}>
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        sx={{
                          minHeight: 500,
                          backgroundColor: snapshot.isDraggingOver ? '#f5f5f5' : 'transparent',
                          borderRadius: 1,
                          p: 1,
                        }}
                      >
                        {tickets[column.id]
                          .sort((a, b) => b.votes - a.votes) // Sort by votes descending
                          .map((ticket, index) => (
                            <Draggable
                              key={ticket.id}
                              draggableId={ticket.id}
                              index={index}
                              isDragDisabled={!isAdmin}
                            >
                              {(provided, snapshot) => (
                                <Card
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  sx={{
                                    mb: 2,
                                    cursor: isAdmin ? 'grab' : 'default',
                                    transform: snapshot.isDragging ? 'rotate(5deg)' : 'none',
                                    boxShadow: snapshot.isDragging ? 3 : 1,
                                  }}
                                >
                                  <CardContent sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                                      {isAdmin && (
                                        <Box {...provided.dragHandleProps} sx={{ mr: 1, mt: 0.5 }}>
                                          <DragIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                        </Box>
                                      )}
                                      
                                      <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                          {ticket.type === 'bug' ? (
                                            <BugIcon sx={{ fontSize: 16, color: '#f44336', mr: 0.5 }} />
                                          ) : (
                                            <FeatureIcon sx={{ fontSize: 16, color: '#ff9800', mr: 0.5 }} />
                                          )}
                                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            {ticket.title}
                                          </Typography>
                                        </Box>
                                        
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                          {ticket.description}
                                        </Typography>
                                        
                                        <Stack direction="row" spacing={1} alignItems="center">
                                          <Avatar sx={{ width: 20, height: 20, fontSize: 10 }}>
                                            {ticket.author.name.charAt(0)}
                                          </Avatar>
                                          <Typography variant="caption" color="text.secondary">
                                            {ticket.author.name}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {new Date(ticket.createdAt).toLocaleDateString()}
                                          </Typography>
                                        </Stack>
                                      </Box>
                                      
                                      {canDelete(ticket) && (
                                        <IconButton
                                          size="small"
                                          onClick={() => handleDeleteTicket(ticket.id, column.id)}
                                          sx={{ ml: 1 }}
                                        >
                                          <DeleteIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                      )}
                                    </Box>
                                    
                                    <Divider sx={{ my: 1 }} />
                                    
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                      <Button
                                        size="small"
                                        startIcon={<ThumbUpIcon />}
                                        onClick={() => handleVote(ticket.id, column.id)}
                                        variant={ticket.votedBy.includes(userId) ? 'contained' : 'outlined'}
                                        sx={{ fontSize: 11 }}
                                      >
                                        {ticket.votes}
                                      </Button>
                                      
                                      <Chip
                                        label={ticket.type === 'bug' ? 'Bug' : 'Feature'}
                                        size="small"
                                        color={ticket.type === 'bug' ? 'error' : 'warning'}
                                        variant="outlined"
                                      />
                                    </Box>
                                  </CardContent>
                                </Card>
                              )}
                            </Draggable>
                          ))}
                        {provided.placeholder}
                      </Box>
                    )}
                  </Droppable>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </DragDropContext>

      {/* Create Ticket Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit Feedback</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Feedback Type
              </Typography>
              <RadioGroup
                value={newTicket.type}
                onChange={(e) => setNewTicket(prev => ({ ...prev, type: e.target.value }))}
                row
              >
                <FormControlLabel value="bug" control={<Radio />} label="Bug Report" />
                <FormControlLabel value="feature" control={<Radio />} label="Feature Request" />
              </RadioGroup>
            </FormControl>

            <TextField
              fullWidth
              label="Title"
              value={newTicket.title}
              onChange={(e) => setNewTicket(prev => ({ ...prev, title: e.target.value }))}
              sx={{ mb: 2 }}
              placeholder="Brief description of the issue or feature"
            />

            <TextField
              fullWidth
              label="Description"
              value={newTicket.description}
              onChange={(e) => setNewTicket(prev => ({ ...prev, description: e.target.value }))}
              multiline
              rows={4}
              placeholder="Provide detailed information about the bug or feature request"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateTicket}
            variant="contained"
            disabled={!newTicket.title.trim() || !newTicket.description.trim()}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}